import { observer } from "mobx-react";
import { useState } from "react";
import { MomentServiceInstance } from "../../services/moment";
import { StorageServiceInstance } from "../../services/storage";
import { createGamedayStoryRequest } from "../../axios/routes/gamedayStory";
import { getSuiteName } from "../../utils/suite";
import { DownloadPromptCopy } from "../../shared/interfaces/team";
import { downloadPromptText } from "../../utils/downloadPromptCopy";
import {
  StoryCheckboxRow,
  StoryErrorText,
  StoryFieldLabel,
  StoryInput,
  StoryLaterButton,
  StoryPopupBody,
  StoryPopupCard,
  StoryPopupHeadline,
  StoryPopupImage,
  StoryPopupOverlay,
  StoryPrimaryButton,
  StoryTextArea,
} from "./styled";

interface Props {
  teamColor?: string;
  copy?: DownloadPromptCopy;
  imageUrl?: string;
  defaultName?: string;
  defaultEmail?: string;
  onDismiss: () => void;
}

type Step = "prompt" | "form" | "thanks";

function buildStoryContext() {
  const s = StorageServiceInstance;
  const g = s.selectedGameData;
  const moment = MomentServiceInstance.selectedMoment;
  const slide = MomentServiceInstance.selectedMomentoSlide || 0;
  const link = moment?.momentoLinks?.[slide];
  const photoUrl = typeof link === "string" && /^https?:\/\//i.test(link) ? link : undefined;
  const photoId = moment?._id
    ? `${moment._id}#${slide}`
    : undefined;
  const t1 = g?.team1?.name || "Home";
  const t2 = g?.team2?.name || "Away";
  return {
    teamId: g?.team1?._id,
    teamName: g?.team1?.name,
    gameId: g?._id,
    gameName: g?._id ? `${t1} vs ${t2}` : undefined,
    eventDate: g?.startDate,
    userId: s.selectedCustomerId || s.selectedRegistrationUserId || undefined,
    photoId,
    photoUrl,
    section: s.selectedSection || undefined,
    row: s.selectedRow || undefined,
    seat: s.selectedSeat || undefined,
    suite: s.showSuites && s.selectedSuite ? (getSuiteName(s.selectedSuite) || s.selectedSuite) : undefined,
    specialSection: s.selectedSectionIsSpecial ? s.selectedSpecialSection : undefined,
  };
}

const GamedayStoryPromptPopup = observer(({
  teamColor,
  copy,
  imageUrl,
  defaultName,
  defaultEmail,
  onDismiss,
}: Props): JSX.Element => {
  const headline = downloadPromptText(copy, "storyHeadline");
  const body = downloadPromptText(copy, "storyBody");
  const [step, setStep] = useState<Step>("prompt");
  const [story, setStory] = useState("");
  const [name, setName] = useState(defaultName || "");
  const [email, setEmail] = useState(defaultEmail || "");
  const [permissionToContact, setPermissionToContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    const trimmed = story.trim();
    if (!trimmed) {
      setError("Please tell us about your gameday.");
      return;
    }
    const emailTrim = email.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setError("Enter a valid email or leave it blank.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const ctx = buildStoryContext();
      await createGamedayStoryRequest({
        story: trimmed,
        name: name.trim() || undefined,
        email: emailTrim || undefined,
        permissionToContact,
        teamId: ctx.teamId,
        teamName: ctx.teamName,
        gameId: ctx.gameId,
        gameName: ctx.gameName,
        eventDate: ctx.eventDate,
        userId: ctx.userId,
        photoId: ctx.photoId,
        photoUrl: ctx.photoUrl,
        section: ctx.section,
        row: ctx.row,
        seat: ctx.seat,
        suite: ctx.suite,
        specialSection: ctx.specialSection,
      });
      setStep("thanks");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not submit your story. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StoryPopupOverlay>
      <StoryPopupCard role="dialog" aria-labelledby="gameday-story-prompt-title">
        {step === "prompt" && (
          <>
            {imageUrl ? <StoryPopupImage src={imageUrl} alt="" /> : null}
            <StoryPopupHeadline id="gameday-story-prompt-title">
              {headline}
            </StoryPopupHeadline>
            <StoryPopupBody>
              {body}
            </StoryPopupBody>
            <StoryPrimaryButton type="button" $teamColor={teamColor} onClick={() => setStep("form")}>
              Share My Story
            </StoryPrimaryButton>
            <StoryLaterButton type="button" onClick={onDismiss}>
              Maybe Later
            </StoryLaterButton>
          </>
        )}
        {step === "form" && (
          <>
            {imageUrl ? <StoryPopupImage src={imageUrl} alt="" /> : null}
            <StoryPopupHeadline id="gameday-story-prompt-title">
              {headline}
            </StoryPopupHeadline>
            <StoryFieldLabel htmlFor="gameday-story-text">Tell us about your gameday</StoryFieldLabel>
            <StoryTextArea
              id="gameday-story-text"
              value={story}
              onChange={(e) => setStory(e.target.value)}
              maxLength={4000}
              placeholder="Share what made today special..."
            />
            <StoryFieldLabel htmlFor="gameday-story-name">Name (optional)</StoryFieldLabel>
            <StoryInput
              id="gameday-story-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
            />
            <StoryFieldLabel htmlFor="gameday-story-email">Email (optional)</StoryFieldLabel>
            <StoryInput
              id="gameday-story-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
            />
            <StoryCheckboxRow>
              <input
                type="checkbox"
                checked={permissionToContact}
                onChange={(e) => setPermissionToContact(e.target.checked)}
              />
              <span>Momento may contact me about my story.</span>
            </StoryCheckboxRow>
            {error ? <StoryErrorText>{error}</StoryErrorText> : null}
            <StoryPrimaryButton
              type="button"
              $teamColor={teamColor}
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? "Submitting..." : "Submit Story"}
            </StoryPrimaryButton>
            <StoryLaterButton type="button" onClick={onDismiss}>
              Maybe Later
            </StoryLaterButton>
          </>
        )}
        {step === "thanks" && (
          <>
            <StoryPopupHeadline id="gameday-story-prompt-title">
              Thanks for sharing your story!
            </StoryPopupHeadline>
            <StoryPopupBody>
              We love hearing what this game meant to you.
            </StoryPopupBody>
            <StoryPrimaryButton type="button" $teamColor={teamColor} onClick={onDismiss}>
              Done
            </StoryPrimaryButton>
          </>
        )}
      </StoryPopupCard>
    </StoryPopupOverlay>
  );
});

export default GamedayStoryPromptPopup;
